/**
 * Border Renderer
 *
 * Creates mesh-based rectangular border outlines using thin box strips.
 * Uses BoxGeometry instead of LineSegments because WebGL linewidth is
 * clamped to 1px on most platforms. Each edge is a thin box lying flat.
 *
 * For dashed style, creates evenly spaced segments along each edge.
 */

import * as THREE from 'three'
import { FEET_TO_METERS } from '@/constants/units'

/**
 * Border rendering options
 */
export interface BorderRenderOptions {
  color?: string
  style?: 'solid' | 'dashed'
  /** Border strip width in feet (default: 2) */
  widthFt?: number
  /** Y offset in feet (default: 0.05 to sit above ground) */
  yOffset?: number
  /** Dash length in feet for dashed style (default: 8) */
  dashSizeFt?: number
  /** Gap length in feet for dashed style (default: 4) */
  gapSizeFt?: number
}

/**
 * Border dimensions
 */
export interface BorderDimensions {
  width: number   // feet (X-axis)
  depth: number   // feet (Z-axis)
}

/** Thin height for border strips (meters) — just visible above ground */
const STRIP_HEIGHT = 0.05

/**
 * Create a single solid border strip as a thin box mesh
 */
function createStrip(
  lengthM: number,
  widthM: number,
  material: THREE.MeshBasicMaterial,
  position: THREE.Vector3,
  rotateY: number = 0
): THREE.Mesh {
  const geo = new THREE.BoxGeometry(lengthM, STRIP_HEIGHT, widthM)
  const mesh = new THREE.Mesh(geo, material)
  mesh.position.copy(position)
  if (rotateY !== 0) mesh.rotation.y = rotateY
  mesh.receiveShadow = true
  return mesh
}

/**
 * Create dashed segments along one edge
 */
function createDashedStrips(
  edgeLengthM: number,
  widthM: number,
  material: THREE.MeshBasicMaterial,
  startPos: THREE.Vector3,
  axis: 'x' | 'z',
  dashM: number,
  gapM: number
): THREE.Mesh[] {
  const meshes: THREE.Mesh[] = []
  const segmentLen = dashM + gapM
  let offset = -edgeLengthM / 2

  while (offset < edgeLengthM / 2) {
    const dashEnd = Math.min(offset + dashM, edgeLengthM / 2)
    const actualDash = dashEnd - offset
    if (actualDash <= 0) break

    const centerOffset = offset + actualDash / 2
    const pos = startPos.clone()
    if (axis === 'x') pos.x += centerOffset
    else pos.z += centerOffset

    const geo = new THREE.BoxGeometry(
      axis === 'x' ? actualDash : widthM,
      STRIP_HEIGHT,
      axis === 'z' ? actualDash : widthM
    )
    const mesh = new THREE.Mesh(geo, material)
    mesh.position.copy(pos)
    mesh.receiveShadow = true
    meshes.push(mesh)

    offset += segmentLen
  }

  return meshes
}

/**
 * Render a rectangular border as mesh strips
 *
 * Creates 4 border edges (thin box strips) forming a rectangle centered at origin.
 * The rectangle lies in the XZ plane at the specified Y offset.
 */
export function renderBorder(
  dimensions: BorderDimensions,
  options: BorderRenderOptions = {}
): THREE.Group {
  const {
    color = '#FFD700',
    style = 'solid',
    widthFt = 2,
    yOffset = 0.05,
    dashSizeFt = 8,
    gapSizeFt = 4,
  } = options

  const halfW = (dimensions.width * FEET_TO_METERS) / 2
  const halfD = (dimensions.depth * FEET_TO_METERS) / 2
  const stripW = widthFt * FEET_TO_METERS
  const halfStrip = stripW / 2
  const y = yOffset * FEET_TO_METERS

  const material = new THREE.MeshBasicMaterial({ color })

  const group = new THREE.Group()
  group.name = 'border'

  if (style === 'solid') {
    // Front edge (+Z) — runs along X
    group.add(createStrip(dimensions.width * FEET_TO_METERS + stripW, stripW, material,
      new THREE.Vector3(0, y, halfD + halfStrip)))
    // Back edge (-Z) — runs along X
    group.add(createStrip(dimensions.width * FEET_TO_METERS + stripW, stripW, material,
      new THREE.Vector3(0, y, -halfD - halfStrip)))
    // Left edge (-X) — runs along Z
    group.add(createStrip(stripW, dimensions.depth * FEET_TO_METERS, material,
      new THREE.Vector3(-halfW - halfStrip, y, 0)))
    // Right edge (+X) — runs along Z
    group.add(createStrip(stripW, dimensions.depth * FEET_TO_METERS, material,
      new THREE.Vector3(halfW + halfStrip, y, 0)))
  } else {
    // Dashed: create segments along each edge
    const dashM = dashSizeFt * FEET_TO_METERS
    const gapM = gapSizeFt * FEET_TO_METERS

    // Front edge (+Z)
    createDashedStrips(
      dimensions.width * FEET_TO_METERS, stripW, material,
      new THREE.Vector3(0, y, halfD + halfStrip), 'x', dashM, gapM
    ).forEach(m => group.add(m))

    // Back edge (-Z)
    createDashedStrips(
      dimensions.width * FEET_TO_METERS, stripW, material,
      new THREE.Vector3(0, y, -halfD - halfStrip), 'x', dashM, gapM
    ).forEach(m => group.add(m))

    // Left edge (-X)
    createDashedStrips(
      dimensions.depth * FEET_TO_METERS, stripW, material,
      new THREE.Vector3(-halfW - halfStrip, y, 0), 'z', dashM, gapM
    ).forEach(m => group.add(m))

    // Right edge (+X)
    createDashedStrips(
      dimensions.depth * FEET_TO_METERS, stripW, material,
      new THREE.Vector3(halfW + halfStrip, y, 0), 'z', dashM, gapM
    ).forEach(m => group.add(m))
  }

  return group
}
